import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Mail, MessageCircle } from "lucide-react";
import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon";
import { trackEvent } from "@/lib/track";
import { trackGoogleEvent } from "@/lib/googleTags";
import { isStudioPreview } from "@/lib/studioPreviewBridge";
import { contactTarget, visibleOnPath, toInt, safeColor, FLOATING_CONTACT_TYPE } from "@/lib/floatingContact";

// Bouton de contact flottant (« Besoin d'aide ? ») — composant COMMUN à tous
// les moteurs de thème, monté une seule fois par Layout.jsx. Ses réglages
// sont ceux de la section « Bouton de contact flottant » du pied de page
// (Studio de thème), voir lib/floatingContact.js pour le lien et la
// visibilité.
//
// Remplace l'ancien bouton WhatsApp codé en dur dans le pied de page du seul
// moteur "default" (thème Assurance) : les moteurs qui rendent leur propre
// pied de page (agence_v1, couture_v1) ne l'affichaient jamais.

const ICONS = { whatsapp: WhatsAppIcon, phone: Phone, email: Mail, link: MessageCircle };

export default function FloatingContactButton({ section, aboveMobileNav = false }) {
  const { pathname } = useLocation();
  const preview = isStudioPreview();
  const props = section?.props || {};
  const delay = preview ? 0 : toInt(props.float_delay, 0, 0, 30);
  const [shown, setShown] = useState(delay === 0);

  useEffect(() => {
    if (delay === 0) {
      setShown(true);
      return undefined;
    }
    setShown(false);
    const timer = setTimeout(() => setShown(true), delay * 1000);
    return () => clearTimeout(timer);
  }, [delay]);

  if (!section || section.enabled === false) return null;
  if (!visibleOnPath(props.float_pages, pathname)) return null;
  const target = contactTarget(props);
  // Dans l'aperçu du Studio, le bouton reste visible tant qu'il n'a pas de
  // cible : c'est justement le moment où l'admin le configure.
  if (!target && !preview) return null;
  if (!shown) return null;

  const channel = props.float_channel || "whatsapp";
  const Icon = ICONS[channel] || MessageCircle;
  const label = String(props.float_label || "").trim();
  const labelOnMobile = props.float_label_mobile !== "false";
  const left = props.float_position === "left";
  const offset = toInt(props.float_offset, 20, 8, 160);
  const background = safeColor(props.float_bg_color, "#25D366");
  const color = safeColor(props.float_text_color, "#FFFFFF");
  const pulse = props.float_pulse === "true";

  const onClick = (e) => {
    if (preview) {
      e.preventDefault();
      return;
    }
    trackEvent("floating_contact_click", { channel });
    // Clic sur le bouton de contact : utile dans Google Analytics / Tag
    // Manager, mais ce n'est PAS une demande envoyée (generate_lead).
    trackGoogleEvent("contact_click", { method: channel, link_url: target?.href });
  };

  const className = [
    "fixed z-40 inline-flex items-center gap-2 rounded-full shadow-xl transition-opacity hover:opacity-90",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ink",
    label ? (labelOnMobile ? "pl-4 pr-5 py-3.5" : "p-3.5 sm:pl-4 sm:pr-5 sm:py-3.5") : "p-3.5",
    "text-[13.5px] font-semibold",
    left ? "left-5" : "right-5",
    // Distance au bas : réglage de la boutique + hauteur du menu mobile.
    "bottom-[calc(var(--fcb-offset,20px)+var(--fcb-extra,0px))]",
    // Au-dessus du menu mobile du bas quand il est affiché (moteur "default").
    aboveMobileNav ? "[--fcb-extra:calc(4rem+env(safe-area-inset-bottom))] md:[--fcb-extra:0px]" : "[--fcb-extra:env(safe-area-inset-bottom)]",
    !target ? "outline-dashed outline-2 outline-offset-2 outline-white/80" : "",
  ].join(" ");

  const content = (
    <>
      {pulse && (
        <span aria-hidden="true" className="absolute inset-0 -z-10 rounded-full motion-safe:animate-ping opacity-40" style={{ background }} />
      )}
      <Icon className="h-5 w-5 shrink-0" />
      {label && <span className={labelOnMobile ? "" : "hidden sm:inline"}>{label}</span>}
    </>
  );

  const shared = {
    className,
    style: { background, color, "--fcb-offset": `${offset}px` },
    onClick,
    "data-testid": "whatsapp-float-button",
    "data-channel": channel,
    "data-section-key": section.id || FLOATING_CONTACT_TYPE,
    "aria-label": label || (channel === "whatsapp" ? "Nous écrire sur WhatsApp" : "Nous contacter"),
    title: !target ? "Renseignez la cible du bouton dans ses réglages" : undefined,
  };

  if (target?.internal && !preview) {
    return <Link to={target.href} {...shared}>{content}</Link>;
  }
  return (
    <a
      href={target?.href || "#"}
      {...(target?.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      {...shared}
    >
      {content}
    </a>
  );
}
